import React, { useCallback, useState } from "react";
import isEmpty from "lodash/isEmpty";

import { getBaseUnits, getUnitOptions } from "utils/fields";
import { getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { useStateSelector } from "utils/field";

import ICEVehicle from "./ICEVehicle";
import HybridVehicle from "./HybridVehicle";
import ElectricVehicle from "./ElectricVehicle";
import AddVehicle from "./AddVehicle";
import TransportationTour from "./TransportationTour";

import EmptyState from "components/EmptyState";
import Field from "components/Field";
import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";

import classes from "./style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

import OpenIcon from "assets/images/icons/JSX/Open";

// Validations
const fuelPriceValidation = getValidation("fuelPriceVehicle");

const renderVehicleComponent = (vehicleType, props) => {
    switch (vehicleType) {
        case "ice":
            return <ICEVehicle {...props} />;
        case "ev":
            return <ElectricVehicle {...props} />;
        case "hybrid":
            return <HybridVehicle {...props} />;
        default:
            return null;
    }
};

export default ({ accessor, change, formName, isUpgrade = false }) => {
    const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};
    const contentOpen = useStateSelector(`model.contentOpen`) || false;
    const [tourForceOpen, setTourForceOpen] = useState(false);

    const fuelPriceUnits = useStateSelector(`${accessor}.fuelPrice_u`);

    const vehicles = useStateSelector(`${accessor}.vehicles`) || {};
    const regularGasPrice = useStateSelector(`${accessor}.regularGasPrice`) || 1.45;
    const premiumGasPrice = useStateSelector(`${accessor}.premiumGasPrice`) || 1.7;
    const dieselPrice = useStateSelector(`${accessor}.dieselPrice`) || 1.7;
    const electricityPrice = useStateSelector(`${accessor}.electricityPrice`) || 0.16;

    const numVehicles = Object.keys(vehicles).length;
    const firstEvId = Object.keys(vehicles).find((id) => vehicles[id]?.type === "ev");

    const handleTourStart = useCallback(() => setTourForceOpen(true), []);
    const handleTourEnd = useCallback(() => setTourForceOpen(false), []);

    return (
        <div className={!isUpgrade && sharedClasses.section}>
            {!isUpgrade && (
                <TransportationTour
                    hasElectricVehicle={Boolean(firstEvId)}
                    onTourStart={handleTourStart}
                    onTourEnd={handleTourEnd}
                />
            )}
            <div className={classes.fuelInfo}>
                <h4 style={{ marginBottom: "1rem" }}>Enter data for vehicle energy consumption.</h4>
                <a
                    style={{ marginBottom: "1rem" }}
                    rel="noopener noreferrer"
                    href="https://natural-resources.canada.ca/energy-efficiency/transportation-alternative-fuels/personal-vehicles/choosing-right-vehicle/buying-electric-vehicle/understanding-the-tables/21383"
                    target="_blank"
                    className={sharedClasses.externalLink}
                >
                    See estimated annual fuel costs <OpenIcon />
                </a>
            </div>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.regularGasPrice`}
                    label="Regular Gas Price ($/L)"
                    validate={fuelPriceValidation}
                    decimals={getDecimalPlaces("fuelPriceVehicle")}
                    change={change}
                    units={{
                        base: getBaseUnits("fuelPriceVehicle", modelUnits),
                        options: getUnitOptions("price"),
                        selected: fuelPriceUnits,
                        unitType: "price",
                        accessor: `${accessor}.fuelPriceUnits_u`,
                    }}
                    setValue={regularGasPrice}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.premiumGasPrice`}
                    label="Premium Gas Price ($/L)"
                    validate={fuelPriceValidation}
                    decimals={getDecimalPlaces("fuelPriceVehicle")}
                    change={change}
                    units={{
                        base: getBaseUnits("fuelPriceVehicle", modelUnits),
                        options: getUnitOptions("price"),
                        selected: fuelPriceUnits,
                        unitType: "price",
                        accessor: `${accessor}.fuelPriceUnits_u`,
                    }}
                    setValue={premiumGasPrice}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.dieselPrice`}
                    label="Diesel Price ($/L)"
                    validate={fuelPriceValidation}
                    decimals={getDecimalPlaces("fuelPriceVehicle")}
                    change={change}
                    units={{
                        base: getBaseUnits("fuelPriceVehicle", modelUnits),
                        options: getUnitOptions("price"),
                        selected: fuelPriceUnits,
                        unitType: "price",
                        accessor: `${accessor}.fuelPriceUnits_u`,
                    }}
                    setValue={dieselPrice}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.electricityPrice`}
                    label="Electricity Price ($/kWh)"
                    //placeholder={0.16}
                    validate={fuelPriceValidation}
                    decimals={getDecimalPlaces("fuelPriceVehicle")}
                    change={change}
                    units={{
                        base: getBaseUnits("fuelPriceVehicle", modelUnits),
                        options: getUnitOptions("price"),
                        selected: fuelPriceUnits,
                        unitType: "price",
                        accessor: `${accessor}.fuelPriceUnits_u`,
                    }}
                    setValue={electricityPrice}
                />
            </InputRow>
            <div className={classes.title}>
                <h4>Vehicles</h4>
                <AddVehicle accessor={accessor} change={change} numVehicles={numVehicles} formName={formName} />
            </div>
            {isEmpty(vehicles) && <EmptyState title="No vehicles to display" style={{}} />}
            {!isEmpty(vehicles) &&
                Object.keys(vehicles).map((id) => {
                    const vehicle = vehicles[id];
                    const vehicleType = vehicle?.type;
                    return renderVehicleComponent(vehicleType, {
                        accessor: `${accessor}.vehicles.${id}`,
                        regularGasPrice,
                        premiumGasPrice,
                        dieselPrice,
                        electricityPrice,
                        change,
                        key: id,
                        formName,
                        forceOpen: contentOpen || (tourForceOpen && id === firstEvId),
                    });
                })}
        </div>
    );
};
